import z from 'zod';

import { DateStringSchema, LocalDateTimeStringSchema, TimeStringSchema } from '../../../../common';
import { ExperienceVersion } from '../../../../experience/const';
import { buildQueryString } from '../../../../utils/querystring';
import { ListingAddOnUnavailableReason } from '../../../add-on/listing-add-on';
import { LanguageCode, ProductLine } from '../../../brand/brand';
import { makeEndpoint } from '../../../common';
import {
  BookingAvailabilityModeValue,
  ExternalFieldSchema,
  IntegrationMetaSchema,
  ShouldValidateComplimentaryBookingSchema,
} from '../../../experience/experiences';
import { LimitedTimeSchema } from '../../../experience/limited-time';
import { PaymentMethods } from '../../../payment-method/public';
import { ScheduleType } from '../../../resource/schedule-time-frame';
import { ListingKind, PublicListingSchema } from '../../listing';
import {
  AddOnSchemaV3,
  CategorySchemaV3,
  HostedBySchemaV3,
  LocationSchemaV3,
  MediasSchemaV3,
  PointsEligibleSchemaV3,
  ResourceGroupSchemaV3,
  TaxSchemaV3,
  VibesSchemaV3,
} from '../common';

export const GetListingV3PublicQuerySchema = z.object({
  language: z.nativeEnum(LanguageCode).default(LanguageCode.EN_US),
  version: z.nativeEnum(ExperienceVersion).default(ExperienceVersion.PUBLISHED),
});

export const GetListingV3PublicResponseSchema = z.object({
  addOns: z.array(AddOnSchemaV3.optional().nullable()),
  advertisedPrice: z.number().optional().nullable(),
  agenda: z.string().optional().nullable(),
  bookingAvailabilityMode: z.nativeEnum(BookingAvailabilityModeValue).nullable(),
  brandId: z.string().uuid().nullable(),
  providerBrandId: z.string().uuid().optional().nullable(),
  cancellationPolicyId: z.string().uuid().optional().nullable(),
  category: CategorySchemaV3.optional().nullable(),
  currency: z.string().optional().nullable(),
  customCallToAction: z.string().optional().nullable(),
  description: z.string().optional().nullable(),
  displayCapacity: z.number().optional().nullable(),
  eventDates: z.array(DateStringSchema),
  eventStartTimes: z.array(TimeStringSchema),
  experienceId: z.string().uuid().optional().nullable(),
  externalField: ExternalFieldSchema.nullable(),
  healthAndSafety: z.string().optional().nullable(),
  hidePrice: z.boolean(),
  hostedBy: HostedBySchemaV3.optional().nullable(),
  limitedTime: LimitedTimeSchema.omit({ createdAt: true }).nullable().optional(),
  id: z.string().uuid(),
  included: z.array(z.string()),
  isBookable: z.boolean(),
  isExclusive: z.boolean(),
  isFeatured: z.boolean(),
  isMapped: z.boolean(),
  isUnlisted: z.boolean(),
  kind: z.nativeEnum(ListingKind),
  location: LocationSchemaV3.optional().nullable(),
  maximumAllowedToBook: z.number().optional().nullable(),
  maxParticipantCount: z.number().optional().nullable(),
  medias: z.array(MediasSchemaV3),
  menu: z.string().optional().nullable(),
  minimumRequiredToBook: z.number().nonnegative().optional().nullable(),
  /** Hours before a session starts that online booking closes; negative means until the session ends. Experience listings only. */
  onlineBookingCutoffHours: z.number().optional().nullable(),
  partySize: z.number().optional().nullable(),
  paymentMethods: z.array(z.nativeEnum(PaymentMethods)).optional().nullable(),
  pointsEligible: PointsEligibleSchemaV3.optional().nullable(),
  productLine: z.nativeEnum(ProductLine),
  ratings: PublicListingSchema.shape.ratings,
  reschedulePolicyId: z.string().uuid().optional().nullable(),
  resourceGroupCollectionId: z.string().uuid().optional().nullable(),
  resourceGroups: z.array(ResourceGroupSchemaV3),
  scheduleType: z.nativeEnum(ScheduleType).optional().nullable(),
  slug: z.string().optional().nullable(),
  startingPrice: z.number().optional().nullable(),
  summary: z.string().optional().nullable(),
  taxes: z.array(TaxSchemaV3),
  title: z.string().optional().nullable(),
  vibes: z.array(VibesSchemaV3),
  integrationMeta: z.array(IntegrationMetaSchema).nullable().optional(),
  shouldValidateComplimentaryBooking: ShouldValidateComplimentaryBookingSchema.optional().nullable(),
  waitlistId: z.string().uuid().optional().nullable(),
});

export type GetListingV3PublicResponse = z.infer<typeof GetListingV3PublicResponseSchema>;

export const GetListingV3PublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.input<typeof GetListingV3PublicQuerySchema> }) =>
    `v3/listings/${listingId}?${buildQueryString(query)}`,
  responseSchema: GetListingV3PublicResponseSchema,
  querySchema: GetListingV3PublicQuerySchema,
  requestSchema: z.void(),
});

export { ListingAddOnUnavailableReason } from '../../../add-on/listing-add-on';

export const GetListingAddOnAvailabilityPublicQuerySchema = z.object({
  experienceId: z.string().uuid(),
  startDateTime: LocalDateTimeStringSchema,
  sessionDuration: z.coerce.number().positive(),
});

export const GetListingAddOnAvailabilityPublicResponseSchema = z.object({
  items: z.array(
    z.object({
      listingAddOnId: z.string().uuid(),
      available: z.boolean(),
      availableQuantity: z.number().nonnegative().nullable(),
      unavailableReason: z.nativeEnum(ListingAddOnUnavailableReason).optional(),
    }),
  ),
});

export const GetListingAddOnAvailabilityPublicEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ listingId, query }: { listingId: string; query: z.input<typeof GetListingAddOnAvailabilityPublicQuerySchema> }) =>
    `v3/listings/${listingId}/add-ons/availability?${buildQueryString(query)}`,
  responseSchema: GetListingAddOnAvailabilityPublicResponseSchema,
  querySchema: GetListingAddOnAvailabilityPublicQuerySchema,
  requestSchema: z.void(),
});
